import { EProgrammingStatus, Programming } from '../../models/programming';
import { normalizeProgrammingStatus } from '../../shared/utils/torre-tracking-utils';
import { plannedVsActualDeltaMinutes, plannedVsNowDeltaMinutes } from '../../utils/programming-timeline-utils';

export const TORRE_OPERATIONAL_ORDER_BY = 'torre_operational';

export const TORRE_WINDOW_MODE_HOJE = 'hoje';
export const TORRE_WINDOW_MODE_PERIODO = 'periodo';
export const DEFAULT_TORRE_TERMINAL_LOOKBACK_DAYS = 2;

export type TorreOperationalGroupKey =
    | 'saindo'
    | 'emTransito'
    | 'chegaram'
    | 'liberados'
    | 'atencao'
    | 'canceladas';

export type TorreOperationalHighlightColumn = 'prevSaida' | 'prevCheg' | 'none';

export interface TorreArrivalIndicator {
    tone: 'onTime' | 'late' | 'early' | 'unknown';
    label: string;
}

export interface TorreOperationalContext {
    now?: Date;
    platesWithActiveProgramming?: ReadonlySet<string>;
}

export interface TorreOperationalSection {
    key: TorreOperationalGroupKey;
    label: string;
    highlightColumn: TorreOperationalHighlightColumn;
    rows: Programming[];
    collapsible?: boolean;
    defaultCollapsed?: boolean;
}

const GROUP_ORDER: TorreOperationalGroupKey[] = [
    'saindo',
    'emTransito',
    'chegaram',
    'liberados',
    'atencao',
    'canceladas',
];

const GROUP_LABELS: Record<TorreOperationalGroupKey, string> = {
    saindo: 'Saindo',
    emTransito: 'Em trânsito',
    chegaram: 'Chegaram',
    liberados: 'Liberados',
    atencao: 'Atenção / paradas',
    canceladas: 'Canceladas',
};

const GROUP_HIGHLIGHT: Record<TorreOperationalGroupKey, TorreOperationalHighlightColumn> = {
    saindo: 'prevSaida',
    emTransito: 'prevCheg',
    chegaram: 'none',
    liberados: 'none',
    atencao: 'prevSaida',
    canceladas: 'none',
};

const TRANSIT_STATUSES = new Set<number>([
    EProgrammingStatus.LoadedTransit,
    EProgrammingStatus.DelayedTransit,
    EProgrammingStatus.EmptyTransit,
    EProgrammingStatus.InProgress,
    EProgrammingStatus.DelayedArrival,
]);

const PRE_DEPARTURE_STATUSES = new Set<number>([
    EProgrammingStatus.Waiting,
    EProgrammingStatus.Created,
    EProgrammingStatus.Available,
    EProgrammingStatus.Loading,
    EProgrammingStatus.DelayedLoading,
    EProgrammingStatus.DelayedDeparture,
]);

const ATTENTION_DELAY_HOURS = 24;

function resolveNow(ctx?: TorreOperationalContext): Date {
    return ctx?.now ?? new Date();
}

export function buildPlatesWithActiveProgramming(rows: Programming[]): Set<string> {
    const set = new Set<string>();
    for (const row of rows) {
        const status = normalizeProgrammingStatus(row.status);
        if (status === null || status === EProgrammingStatus.Completed
            || status === EProgrammingStatus.Finished
            || status === EProgrammingStatus.Cancelled) {
            continue;
        }
        if (row.mainVehiclePlateId) {
            set.add(row.mainVehiclePlateId);
        }
    }
    return set;
}

function isTerminalStatus(status: number): boolean {
    return status === EProgrammingStatus.Completed
        || status === EProgrammingStatus.Finished
        || status === EProgrammingStatus.Cancelled;
}

function isChegaram(p: Programming): boolean {
    if (!p.actualDestinationEnteredAt) return false;
    const status = normalizeProgrammingStatus(p.status);
    if (status === null || status === EProgrammingStatus.Completed || status === EProgrammingStatus.Finished) {
        return false;
    }
    return status === EProgrammingStatus.Unloading
        || status === EProgrammingStatus.DelayedUnloading
        || status === EProgrammingStatus.DelayedArrival
        || status === EProgrammingStatus.Loading
        || status === EProgrammingStatus.DelayedLoading;
}

function isLiberado(p: Programming, platesWithActive: ReadonlySet<string>): boolean {
    const status = normalizeProgrammingStatus(p.status);
    if (status !== EProgrammingStatus.Completed && status !== EProgrammingStatus.Finished) return false;
    if (!p.actualDestinationEnteredAt) return false;
    if (!p.mainVehiclePlateId) return true;
    return !platesWithActive.has(p.mainVehiclePlateId);
}

function isAtencao(p: Programming, now: Date): boolean {
    const status = normalizeProgrammingStatus(p.status);
    if (status === null || isTerminalStatus(status)) return false;

    const threshold = new Date(now.getTime() - ATTENTION_DELAY_HOURS * 60 * 60 * 1000);
    const expectedDeparture = p.expectedOriginDepartureAt ?? p.expectedDepartureTime;
    const expectedArrival = p.expectedDestinationArrivalAt ?? p.expectedArrivalTime;

    if (expectedDeparture) {
        const depPastNow = plannedVsNowDeltaMinutes(expectedDeparture, now);
        if (depPastNow !== null && depPastNow > 0 && !p.actualOriginExitedAt
            && status !== null && PRE_DEPARTURE_STATUSES.has(status)) {
            return true;
        }
        const depPastThreshold = plannedVsNowDeltaMinutes(expectedDeparture, threshold);
        if (depPastThreshold !== null && depPastThreshold > 0 && !p.actualOriginExitedAt) {
            return true;
        }
    }

    if (expectedArrival) {
        const arrPastThreshold = plannedVsNowDeltaMinutes(expectedArrival, threshold);
        if (arrPastThreshold !== null && arrPastThreshold > 0 && !p.actualDestinationEnteredAt
            && status !== null && TRANSIT_STATUSES.has(status)) {
            return true;
        }
    }

    return false;
}

/**
 * Regra Liberados (espelha backend): Completed/Finished + chegada real ao destino +
 * placa sem outra programação em status operacional (não terminal).
 */
export function getTorreOperationalGroup(
    programming: Programming,
    ctx?: TorreOperationalContext
): TorreOperationalGroupKey {
    const value = normalizeProgrammingStatus(programming.status);
    const now = resolveNow(ctx);
    const plates = ctx?.platesWithActiveProgramming ?? buildPlatesWithActiveProgramming([]);

    if (value === EProgrammingStatus.Cancelled) return 'canceladas';
    if (value !== null && isAtencao(programming, now)) return 'atencao';
    if (isChegaram(programming)) return 'chegaram';
    if (isLiberado(programming, plates)) return 'liberados';
    if (value !== null && TRANSIT_STATUSES.has(value)) return 'emTransito';
    return 'saindo';
}

export function getTorreArrivalIndicator(programming: Programming): TorreArrivalIndicator {
    const expectedRaw = programming.expectedDestinationArrivalAt ?? programming.expectedArrivalTime;
    const actualRaw = programming.actualDestinationEnteredAt;
    if (!expectedRaw || !actualRaw) {
        return { tone: 'unknown', label: '—' };
    }
    const diffMin = plannedVsActualDeltaMinutes(expectedRaw, actualRaw);
    if (diffMin === null) {
        return { tone: 'unknown', label: '—' };
    }
    if (Math.abs(diffMin) < 1) {
        return { tone: 'onTime', label: 'No horário' };
    }
    if (diffMin > 0) {
        return { tone: 'late', label: `Atrasou ${diffMin} min` };
    }
    return { tone: 'early', label: `Adiantado ${Math.abs(diffMin)} min` };
}

function departureProximityKey(p: Programming, now: Date): number {
    const raw = p.expectedOriginDepartureAt ?? p.expectedDepartureTime;
    if (!raw) return Number.MAX_SAFE_INTEGER;
    const delta = plannedVsNowDeltaMinutes(raw, now);
    if (delta === null) return Number.MAX_SAFE_INTEGER;
    return Math.abs(delta);
}

function dateKey(raw?: string): number {
    if (!raw) return Number.MAX_SAFE_INTEGER;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
}

function recencyKey(p: Programming): number {
    const raw = p.updatedAt ?? p.createdAt;
    if (!raw) return 0;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? 0 : t;
}

export function compareTorreOperational(
    a: Programming,
    b: Programming,
    ctx?: TorreOperationalContext
): number {
    const plates = ctx?.platesWithActiveProgramming
        ?? buildPlatesWithActiveProgramming([a, b]);
    const fullCtx: TorreOperationalContext = { ...ctx, platesWithActiveProgramming: plates };
    const now = resolveNow(fullCtx);

    const ga = getTorreOperationalGroup(a, fullCtx);
    const gb = getTorreOperationalGroup(b, fullCtx);
    const groupCmp = GROUP_ORDER.indexOf(ga) - GROUP_ORDER.indexOf(gb);
    if (groupCmp !== 0) return groupCmp;

    if (ga === 'saindo') {
        const cmp = departureProximityKey(a, now) - departureProximityKey(b, now);
        if (cmp !== 0) return cmp;
        return dateKey(a.expectedOriginDepartureAt ?? a.expectedDepartureTime)
            - dateKey(b.expectedOriginDepartureAt ?? b.expectedDepartureTime);
    }
    if (ga === 'emTransito') {
        return dateKey(a.expectedDestinationArrivalAt ?? a.expectedArrivalTime)
            - dateKey(b.expectedDestinationArrivalAt ?? b.expectedArrivalTime);
    }
    if (ga === 'chegaram' || ga === 'liberados') {
        return dateKey(b.actualDestinationEnteredAt) - dateKey(a.actualDestinationEnteredAt);
    }
    if (ga === 'atencao') {
        return dateKey(a.expectedOriginDepartureAt ?? a.expectedDepartureTime)
            - dateKey(b.expectedOriginDepartureAt ?? b.expectedDepartureTime);
    }
    return recencyKey(b) - recencyKey(a);
}

export function sortTorreOperationalRows(
    rows: Programming[],
    ctx?: TorreOperationalContext
): Programming[] {
    const plates = ctx?.platesWithActiveProgramming ?? buildPlatesWithActiveProgramming(rows);
    const fullCtx: TorreOperationalContext = { ...ctx, platesWithActiveProgramming: plates };
    return [...rows].sort((a, b) => compareTorreOperational(a, b, fullCtx));
}

export interface BuildTorreOperationalSectionsOptions {
    ctx?: TorreOperationalContext;
    showCancelled?: boolean;
}

export function buildTorreOperationalSections(
    rows: Programming[],
    options?: BuildTorreOperationalSectionsOptions
): TorreOperationalSection[] {
    if (!rows.length) return [];

    const plates = options?.ctx?.platesWithActiveProgramming ?? buildPlatesWithActiveProgramming(rows);
    const ctx: TorreOperationalContext = { ...options?.ctx, platesWithActiveProgramming: plates };
    const sorted = sortTorreOperationalRows(rows, ctx);
    const sections: TorreOperationalSection[] = [];

    let currentKey: TorreOperationalGroupKey | null = null;
    let bucket: Programming[] = [];

    const flush = () => {
        if (!currentKey || bucket.length === 0) return;
        sections.push({
            key: currentKey,
            label: GROUP_LABELS[currentKey],
            highlightColumn: GROUP_HIGHLIGHT[currentKey],
            rows: bucket,
            collapsible: currentKey === 'canceladas',
            defaultCollapsed: currentKey === 'canceladas',
        });
        bucket = [];
    };

    for (const row of sorted) {
        const key = getTorreOperationalGroup(row, ctx);
        if (key === 'canceladas' && options?.showCancelled === false) {
            continue;
        }
        if (key !== currentKey) {
            flush();
            currentKey = key;
        }
        bucket.push(row);
    }
    flush();

    return sections;
}

export function usesTorreOperationalOrder(orderBy?: string): boolean {
    return orderBy === TORRE_OPERATIONAL_ORDER_BY;
}
